import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { AjsResponse, wrapAjs } from '../ajs-response';
import { buildParams, PAGINATION_SEARCH_FIELDS } from '../http-params.util';

/**
 * ReportingApiService (Angular 18) — datasources, report definitions,
 * synchronous report execution, and async report generation.
 *
 * Replaces apps/b2b-ui/common/services/api/reportingApi/reportingApi.service.js.
 *
 * Four sub-namespaces:
 *   apiService.reporting.datasources.{list,get}
 *   apiService.reporting.definitions.{list,get,create,createCopy,update,remove,duplicate}
 *   apiService.reporting.reports.{execute,query}                       sync execution
 *   apiService.reporting.asyncReports.{create,list,getByReportId,get,getJson,
 *                                       remove,isReportCompleted,csvUrl}
 *
 * Legacy quirks preserved:
 *   - `asyncReports.csvUrl` is exposed as a string property (consumers read it
 *     to compose links), not a method call.
 *   - `asyncReports.create()`, `.get()`, `.getJson()`, `.getByReportId()`
 *     unwrap `response.data.payload` before returning, matching the legacy
 *     `HelpersService.utils.unwrapAcmeResponse` post-then behaviour. These
 *     are the only methods in the file that diverge from the standard
 *     `AjsResponse<T>` wrapped return type.
 *   - `reports.execute()` and `reports.query()` convert any `Date` instances
 *     in `startDate` / `endDate` to `yyyy-M-D` strings before sending.
 *   - `definitions.duplicate(id)` POSTs (no body) — preserved.
 */
const ANALYTICS_BASE_V1 = '/v1/api/analytics';
const ANALYTICS_BASE_V2 = '/v2/api/analytics';
const ASYNC_REPORT_BASE_V2 = '/v2/api/async/report';
const ASYNC_REPORT_CSV_URL = '/v2/api/async/report/csv';

const ASYNC_REPORTS_LIST_FIELDS = [
  'page',
  'pageSize',
  'search',
  'sortDirection',
  'sortField',
  'startTime',
  'endTime',
] as const;

const ASYNC_REPORTS_BY_REPORT_FIELDS = [
  'reportUuid',
  'page',
  'pageSize',
  'sortDirection',
  'sortField',
  'startTime',
  'endTime',
] as const;

const EXECUTE_FIELDS = [
  'reportDefinitionId',
  'limit',
  'dateRangeField',
  'startDate',
  'endDate',
  'startDateTime',
  'endDateTime',
] as const;

const QUERY_FIELDS = [
  'dummy',
  'reportDefinitionId',
  'dateRangeField',
  'startDate',
  'endDate',
  'startDateTime',
  'endDateTime',
] as const;

const ASYNC_STATUS = {
  pending: 'Pending',
  processing: 'Processing',
  completed: 'Completed',
  error: 'Error',
} as const;

interface AsyncReport {
  status?: string;
  [key: string]: unknown;
}

interface ReportExecuteProps {
  id: number | string;
  reportDefinitionId?: number | string;
  limit?: number;
  dateRangeField?: string;
  startDate?: Date | string;
  endDate?: Date | string;
  startDateTime?: string;
  endDateTime?: string;
}

interface ReportQueryProps {
  queryExpression: unknown;
  dummy?: unknown;
  reportDefinitionId?: number | string;
  dateRangeField?: string;
  startDate?: Date | string;
  endDate?: Date | string;
  startDateTime?: string;
  endDateTime?: string;
}

function dateToString(d: Date): string {
  return [d.getFullYear(), d.getMonth() + 1, d.getDate()].join('-');
}

function normalizeDateProps<T extends { startDate?: unknown; endDate?: unknown }>(props: T): T {
  if (props.startDate instanceof Date) props.startDate = dateToString(props.startDate);
  if (props.endDate instanceof Date) props.endDate = dateToString(props.endDate);
  return props;
}

@Injectable({ providedIn: 'root' })
export class ReportingApiService {
  private readonly http = inject(HttpClient);

  readonly datasources = {
    list: (): Promise<AjsResponse<unknown>> => wrapAjs(this.http.get<unknown>(`${ANALYTICS_BASE_V1}/metadata`)),

    get: (name: string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.get<unknown>(`${ANALYTICS_BASE_V1}/metadata/collection/${name}`)),
  };

  readonly definitions = {
    list: (props?: Record<string, unknown>): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(`${ANALYTICS_BASE_V2}/report/definitions`, {
          params: buildParams(props, PAGINATION_SEARCH_FIELDS),
        }),
      ),

    get: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.get<unknown>(`${ANALYTICS_BASE_V1}/report/definitions/${id}`)),

    create: (props: {
      reportName: string;
      reportDescription?: string;
      dataSourceName: string;
    }): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.post<unknown>(`${ANALYTICS_BASE_V1}/report/definitions`, {
          name: props.reportName,
          description: props.reportDescription,
          queryExpression: { collectionName: props.dataSourceName },
        }),
      ),

    createCopy: (props: unknown): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.post<unknown>(`${ANALYTICS_BASE_V1}/report/definitions`, props)),

    update: (props: { id: number | string; [key: string]: unknown }): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.put<unknown>(`${ANALYTICS_BASE_V1}/report/definitions/${props.id}`, props)),

    remove: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.delete<unknown>(`${ANALYTICS_BASE_V1}/report/definitions/${id}`)),

    duplicate: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.post<unknown>(`${ANALYTICS_BASE_V2}/report/definitions/duplicate/${id}`, null)),
  };

  readonly reports = {
    execute: (props: ReportExecuteProps): Promise<AjsResponse<unknown>> => {
      const normalized = normalizeDateProps({ ...props });
      return wrapAjs(
        this.http.get<unknown>(`${ANALYTICS_BASE_V1}/report/execute/${props.id}`, {
          params: buildParams(normalized as Record<string, unknown>, EXECUTE_FIELDS),
        }),
      );
    },

    query: (props: ReportQueryProps): Promise<AjsResponse<unknown>> => {
      const normalized = normalizeDateProps({ ...props });
      return wrapAjs(
        this.http.post<unknown>(`${ANALYTICS_BASE_V1}/report/execute`, props.queryExpression, {
          params: buildParams(normalized as Record<string, unknown>, QUERY_FIELDS),
        }),
      );
    },
  };

  readonly asyncReports = {
    csvUrl: ASYNC_REPORT_CSV_URL,

    // Legacy `.then(HelpersService.utils.unwrapAcmeResponse)` returned the
    // payload directly. Mirror that contract for the four async report
    // methods documented in the file header.
    create: async (props: {
      startDate?: Date | string;
      endDate?: Date | string;
      [key: string]: unknown;
    }): Promise<unknown> => {
      const normalized = normalizeDateProps({ ...props });
      const response = await wrapAjs(this.http.post<{ payload?: unknown }>(ASYNC_REPORT_BASE_V2, normalized));
      return response.data?.payload;
    },

    list: (query: Record<string, unknown>): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(ASYNC_REPORT_BASE_V2, {
          params: buildParams(query, ASYNC_REPORTS_LIST_FIELDS),
        }),
      ),

    getByReportId: async (reportUuid: string, query?: Record<string, unknown>): Promise<unknown> => {
      const response = await wrapAjs(
        this.http.get<{ payload?: unknown }>(ASYNC_REPORT_BASE_V2, {
          params: buildParams({ reportUuid, ...(query ?? {}) }, ASYNC_REPORTS_BY_REPORT_FIELDS),
        }),
      );
      return response.data?.payload;
    },

    /**
     * Returns the report object when it has completed successfully, throws
     * when it has errored, and returns `null` otherwise (preserving the
     * legacy three-state contract: completed → report, error → reject,
     * pending/processing → null).
     */
    isReportCompleted: (asyncReport: AsyncReport): AsyncReport | null => {
      if (asyncReport.status === ASYNC_STATUS.completed) return asyncReport;
      if (asyncReport.status === ASYNC_STATUS.error) throw asyncReport;
      return null;
    },

    get: async (id: number | string): Promise<unknown> => {
      const response = await wrapAjs(this.http.get<{ payload?: unknown }>(`${ASYNC_REPORT_BASE_V2}/${id}`));
      return response.data?.payload;
    },

    getJson: async (id: number | string): Promise<unknown> => {
      const response = await wrapAjs(this.http.get<{ payload?: unknown }>(`${ASYNC_REPORT_BASE_V2}/json/${id}`));
      return response.data?.payload;
    },

    remove: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.delete<unknown>(`${ASYNC_REPORT_BASE_V2}/${id}`)),
  };
}
